import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { MfaEnrolment, MfaRecoveryCodes, ReadUser } from '@tinct/core-api-models';
import {
  Button,
  CopyButton,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { useActivateMfaForm } from '../../hooks/use-activate-mfa-form';
import { MfaCodeInput } from './mfa-code-input';
import { RecoveryCodesList } from './recovery-codes-list';

type EnableMfaModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: ReadUser;
  /** The pending enrolment, obtained by the caller before opening (the secret is returned once). */
  enrolment: MfaEnrolment;
};

/**
 * Two steps: scan the QR code and confirm with the first code, then save the recovery codes.
 * Closing during the first step leaves the enrolment pending but harmless: MFA is not active
 * until a code is confirmed, and the next setup simply replaces it.
 */
export const EnableMfaModal = ({ open, onOpenChange, user, enrolment }: EnableMfaModalProps) => {
  const [recoveryCodes, setRecoveryCodes] = useState<MfaRecoveryCodes | null>(null);
  const { form, handleSubmit, isLoading } = useActivateMfaForm(user, setRecoveryCodes);
  const rootError = form.formState.errors.root?.message;

  if (recoveryCodes) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Two-factor authentication is on</DialogTitle>
            <DialogDescription>
              From now on, signing in asks for a code from your authenticator app.
            </DialogDescription>
          </DialogHeader>
          <RecoveryCodesList codes={recoveryCodes.recoveryCodes} accountEmail={user.email} />
          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              I have saved my recovery codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form id="enable-mfa-form" onSubmit={handleSubmit} className="contents">
          <FormProvider {...form}>
            <DialogHeader>
              <DialogTitle>Set up two-factor authentication</DialogTitle>
              <DialogDescription>
                Scan the QR code with an authenticator app such as Google Authenticator, 1Password
                or Authy, then enter the 6-digit code it shows.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
              <img
                src={enrolment.qrCodeDataUrl}
                alt="QR code to scan with your authenticator app"
                width={176}
                height={176}
                className="size-44 shrink-0 self-center rounded-lg border bg-white p-2"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium">Can't scan it?</span>
                  <span className="text-sm text-muted-foreground">
                    Enter this key by hand in the app instead (time-based, 6 digits).
                  </span>
                  <div className="flex items-center gap-1 rounded-md border bg-muted/40 pl-3">
                    <code className="min-w-0 flex-1 break-all py-2 font-mono text-xs tracking-wider select-all">
                      {enrolment.secret}
                    </code>
                    <CopyButton value={enrolment.secret} toastMessage="Setup key copied" />
                  </div>
                </div>
                <MfaCodeInput name="code" label="Code from the app" autoFocus />
                {rootError && (
                  <p role="alert" className="text-sm text-destructive">
                    {rootError}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" form="enable-mfa-form" isLoading={isLoading}>
                Activate
              </Button>
            </DialogFooter>
          </FormProvider>
        </form>
      </DialogContent>
    </Dialog>
  );
};
